/**
 * Browser-side measurement of the call, read from `RTCPeerConnection.getStats()`.
 *
 * Two conventions matter throughout. WebRTC reports `jitter` and `roundTripTime` in **seconds**,
 * and everything here is milliseconds. And an unknown value stays `null`: reporting a missing
 * counter as zero would turn "we could not measure this" into "this was perfect".
 */

/** One second's reading of the counters that matter. Counters are cumulative for the call. */
export interface StatsSample {
	at: number
	inboundPackets: number
	inboundLost: number
	inboundJitterMs: number | null
	inboundBytes: number
	concealedSamples: number | null
	totalSamplesReceived: number | null
	outboundPackets: number
	outboundBytes: number
	/** What the far end reports about our uplink. */
	remoteLost: number | null
	remoteJitterMs: number | null
	remoteRttMs: number | null
	/** From the nominated candidate pair, which is the more trustworthy RTT. */
	rttMs: number | null
	localCandidateType: string | null
	remoteCandidateType: string | null
	/** Level of the audio this browser is sending, from `media-source`. */
	sourceLevel: number | null
}

export interface BrowserMeasurement {
	inboundPackets: number
	inboundLost: number
	/**
	 * Packets per second over the sampled span.
	 *
	 * This is the figure worth comparing with the platform's, because it does not depend on how
	 * long each side happened to measure for. The platform's counters are read at hangup on about
	 * half of all legs and are otherwise the newest mid-call sample, so its totals can cover a
	 * shorter window than the call — while the rate stays the same.
	 */
	inboundRatePerSecond: number | null
	outboundRatePerSecond: number | null
	/** The span the samples cover, which is the window the rate was measured over. */
	sampledMs: number
	inboundLossPercent: number | null
	inboundJitterMs: number | null
	inboundJitterPeakMs: number | null
	/** Share of audio samples the jitter buffer had to invent. The honest audibility number. */
	concealmentPercent: number | null
	outboundPackets: number
	outboundLossPercent: number | null
	outboundJitterMs: number | null
	rttMs: number | null
	sourcePeakLevel: number | null
	localCandidateType: string | null
	remoteCandidateType: string | null
	bothDirections: boolean
	samples: StatsSample[]
}

/** An RTT beyond this comes from unusable RTCP timestamps, not from a slow network. */
const MAX_CREDIBLE_RTT_MS = 5000

const SECONDS_TO_MS = 1000

export function extractSample(report: RTCStatsReport, at: number): StatsSample {
	const sample: StatsSample = {
		at,
		inboundPackets: 0, inboundLost: 0, inboundJitterMs: null, inboundBytes: 0,
		concealedSamples: null, totalSamplesReceived: null,
		outboundPackets: 0, outboundBytes: 0,
		remoteLost: null, remoteJitterMs: null, remoteRttMs: null,
		rttMs: null, localCandidateType: null, remoteCandidateType: null, sourceLevel: null
	}

	// Candidate ids are resolved in a second pass, once the pair has named them.
	const candidateTypes = new Map<string, string>()
	let localCandidateId: string | null = null
	let remoteCandidateId: string | null = null

	report.forEach((raw) => {
		const stat = raw as Record<string, unknown>

		switch (stat.type) {
			case 'inbound-rtp':
				if (stat.kind !== 'audio') break
				sample.inboundPackets = num(stat.packetsReceived) ?? 0
				sample.inboundLost = num(stat.packetsLost) ?? 0
				sample.inboundJitterMs = scale(num(stat.jitter))
				sample.inboundBytes = num(stat.bytesReceived) ?? 0
				sample.concealedSamples = num(stat.concealedSamples)
				sample.totalSamplesReceived = num(stat.totalSamplesReceived)
				break

			case 'outbound-rtp':
				if (stat.kind !== 'audio') break
				sample.outboundPackets = num(stat.packetsSent) ?? 0
				sample.outboundBytes = num(stat.bytesSent) ?? 0
				break

			case 'remote-inbound-rtp':
				if (stat.kind !== 'audio') break
				sample.remoteLost = num(stat.packetsLost)
				sample.remoteJitterMs = scale(num(stat.jitter))
				sample.remoteRttMs = scale(num(stat.roundTripTime))
				break

			case 'media-source':
				if (stat.kind !== 'audio') break
				sample.sourceLevel = num(stat.audioLevel)
				break

			case 'candidate-pair':
				if (stat.state !== 'succeeded' || !stat.nominated) break
				sample.rttMs = scale(num(stat.currentRoundTripTime))
				localCandidateId = str(stat.localCandidateId)
				remoteCandidateId = str(stat.remoteCandidateId)
				break

			case 'local-candidate':
			case 'remote-candidate': {
				const id = str(stat.id)
				const type = str(stat.candidateType)
				if (id && type) candidateTypes.set(id, type)
				break
			}
		}
	})

	if (localCandidateId) sample.localCandidateType = candidateTypes.get(localCandidateId) ?? null
	if (remoteCandidateId) sample.remoteCandidateType = candidateTypes.get(remoteCandidateId) ?? null

	return sample
}

/** Reduces the per-second samples to the numbers the result screen reports. */
export function summarise(samples: StatsSample[]): BrowserMeasurement {
	const last = samples.at(-1)

	const first = samples[0]
	const sampledMs = first && last ? last.at - first.at : 0

	if (!last || !first) {
		return {
			inboundPackets: 0, inboundLost: 0,
			inboundRatePerSecond: null, outboundRatePerSecond: null, sampledMs: 0,
			inboundLossPercent: null,
			inboundJitterMs: null, inboundJitterPeakMs: null, concealmentPercent: null,
			outboundPackets: 0, outboundLossPercent: null, outboundJitterMs: null,
			rttMs: null, sourcePeakLevel: null,
			localCandidateType: null, remoteCandidateType: null,
			bothDirections: false, samples
		}
	}

	return {
		inboundPackets: last.inboundPackets,
		inboundLost: last.inboundLost,
		inboundRatePerSecond: ratePerSecond(first.inboundPackets, last.inboundPackets, sampledMs),
		outboundRatePerSecond: ratePerSecond(first.outboundPackets, last.outboundPackets, sampledMs),
		sampledMs,
		inboundLossPercent: lossPercent(last.inboundPackets, last.inboundLost),
		inboundJitterMs: last.inboundJitterMs,
		inboundJitterPeakMs: peak(samples, (s) => s.inboundJitterMs),
		concealmentPercent: ratioPercent(last.concealedSamples, last.totalSamplesReceived),
		outboundPackets: last.outboundPackets,
		outboundLossPercent: lossPercent(last.outboundPackets, last.remoteLost),
		outboundJitterMs: last.remoteJitterMs,
		rttMs: credibleRtt(last),
		sourcePeakLevel: peak(samples, (s) => s.sourceLevel),
		localCandidateType: last.localCandidateType,
		remoteCandidateType: last.remoteCandidateType,
		bothDirections: last.inboundPackets > 0 && last.outboundPackets > 0,
		samples
	}
}

/**
 * Loss as a share of what should have arrived. Null when the direction carried nothing at all —
 * "no packets" is a different finding from "no loss", and the caller must not confuse them.
 */
/** Counters are cumulative, so the rate is the growth across the span rather than total/span. */
function ratePerSecond(from: number, to: number, spanMs: number): number | null {
	if (spanMs <= 0) return null
	return (to - from) / (spanMs / 1000)
}

function lossPercent(packets: number, lost: number | null): number | null {
	if (lost === null) return null
	const expected = packets + lost
	if (expected <= 0) return null
	return (lost / expected) * 100
}

function ratioPercent(part: number | null, whole: number | null): number | null {
	if (part === null || whole === null || whole <= 0) return null
	return (part / whole) * 100
}

/** The candidate pair measures the media path; RTCP is the fallback. */
function credibleRtt(sample: StatsSample): number | null {
	const rtt = sample.rttMs ?? sample.remoteRttMs
	if (rtt === null || rtt > MAX_CREDIBLE_RTT_MS) return null
	return rtt
}

function peak(samples: StatsSample[], pick: (sample: StatsSample) => number | null): number | null {
	const values = samples.map(pick).filter((value): value is number => value !== null)
	return values.length > 0 ? Math.max(...values) : null
}

function num(value: unknown): number | null {
	return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function str(value: unknown): string | null {
	return typeof value === 'string' && value.length > 0 ? value : null
}

function scale(seconds: number | null): number | null {
	return seconds === null ? null : seconds * SECONDS_TO_MS
}
